import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

const GST_RATE = 0.05;

export default function CoachDashboard() {
  const { profile, isAdmin, isSeniorCoach } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoInactiveCount, setAutoInactiveCount] = useState(0);

  const base = isAdmin ? '/partner/my-business' : isSeniorCoach ? '/senior/my-business' : '/coach';

  useEffect(() => {
    if (profile?.id) {
      fetchStats();
      runAutoInactiveCheck();
    }
  }, [profile]);

  const runAutoInactiveCheck = async () => {
    // Quietly check and mark clients inactive if no payment in 90 days
    // Only updates clients that need it — lightweight
    try {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const { data: staleClients } = await supabase
        .from('clients')
        .select('id')
        .eq('coach_id', profile.id)
        .eq('status', 'active')
        .lt('last_payment_at', ninetyDaysAgo.toISOString())
        .not('last_payment_at', 'is', null);

      if (staleClients && staleClients.length > 0) {
        const ids = staleClients.map(c => c.id);
        await supabase.from('clients').update({ status: 'inactive' }).in('id', ids);
        setAutoInactiveCount(staleClients.length);
      }
    } catch (err) {
      // Silent fail — non-critical
      console.log('Auto-inactive check skipped:', err.message);
    }
  };

  const fetchStats = async () => {
    try {
      const userId = profile.id;
      const now = new Date();
      const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const [
        activeSubs,
        activeClients,
        mtdTxns,
        allTxns,
        recentTxns,
        products
      ] = await Promise.all([
        supabase.from('subscriptions').select('id, product:product_id(price_amount)', { count: 'exact' })
          .eq('coach_id', userId).eq('status', 'active'),
        supabase.from('clients').select('id', { count: 'exact' })
          .eq('coach_id', userId).eq('status', 'active'),
        supabase.from('transactions').select('coach_amount, gross_amount, gst_amount')
          .eq('coach_id', userId).eq('status', 'completed')
          .gte('created_at', mtdStart.toISOString()),
        supabase.from('transactions').select('coach_amount')
          .eq('coach_id', userId).eq('status', 'completed'),
        supabase.from('transactions').select('*')
          .eq('coach_id', userId).order('created_at', { ascending: false }).limit(5),
        supabase.from('products').select('id', { count: 'exact' })
          .eq('coach_id', userId).eq('status', 'active')
      ]);

      const mtd = mtdTxns.data || [];
      const mtdRevenue = mtd.reduce((s, t) => s + Number(t.coach_amount || 0), 0);
      const mtdGross = mtd.reduce((s, t) => s + Number(t.gross_amount || 0), 0);
      const mtdGst = mtd.reduce((s, t) => s + Number(t.gst_amount || 0), 0);
      const totalRevenue = (allTxns.data || []).reduce((s, t) => s + Number(t.coach_amount || 0), 0);

      // Projected monthly revenue from active subscriptions
      const subsData = activeSubs.data || [];
      const projectedMonthly = subsData.reduce((s, sub) => s + Number(sub.product?.price_amount || 0), 0);

      setRecentTransactions(recentTxns.data || []);
      setStats({
        activeSubscriptions: activeSubs.count || 0,
        activeClients: activeClients.count || 0,
        activeProducts: products.count || 0,
        mtdRevenue,
        mtdGross,
        mtdGst,
        totalRevenue,
        projectedMonthly,
        mtdPayments: mtd.length
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`;
  const monthName = new Date().toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  if (loading) return <div style={{ color: 'var(--text-secondary)', padding: 32 }}>Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, color: 'var(--text-primary)', marginBottom: 8 }}>
          My Business
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Snapshot for <strong style={{ color: 'var(--text-primary)' }}>{monthName}</strong>
        </p>
      </div>

      {/* Auto-inactive notification */}
      {autoInactiveCount > 0 && (
        <div className="alert" style={{
          background: 'rgba(240,165,0,0.08)', border: '1px solid rgba(240,165,0,0.2)',
          color: 'var(--warning)', marginBottom: 20
        }}>
          {autoInactiveCount} client{autoInactiveCount > 1 ? 's were' : ' was'} automatically moved to inactive — no payment in the last 90 days.
          <Link to={`${base}/clients`} style={{ color: 'var(--warning)', marginLeft: 8, textDecoration: 'underline' }}>
            View clients →
          </Link>
        </div>
      )}

      {/* MTD Stats */}
      <div className="stat-grid" style={{ marginBottom: 28 }}>
        {[
          { label: 'My Revenue (MTD)', value: fmt(stats?.mtdRevenue), sub: `${stats?.mtdPayments} payments this month`, icon: '✦', highlight: true },
          { label: 'Gross Revenue (MTD)', value: fmt(stats?.mtdGross), sub: 'before splits', icon: '◇' },
          { label: 'GST Collected (MTD)', value: fmt(stats?.mtdGst), sub: 'for remittance', icon: '◎' },
          { label: 'Active Clients', value: stats?.activeClients, sub: 'paid last 90 days', icon: '◉' },
          { label: 'Active Subscriptions', value: stats?.activeSubscriptions, sub: 'recurring', icon: '◆' },
          { label: 'Projected Monthly', value: fmt(stats?.projectedMonthly), sub: 'from subscriptions', icon: '◃' }
        ].map((s, i) => (
          <div key={i} className="stat-card" style={s.highlight ? { borderColor: 'rgba(201,169,110,0.4)', background: 'rgba(201,169,110,0.05)' } : {}}>
            <div className="stat-label">{s.icon} {s.label}</div>
            <div className="stat-value" style={s.highlight ? { color: 'var(--accent)' } : {}}>{s.value ?? '—'}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
        <Link to={`${base}/products`} className="btn btn-primary">+ Create Product</Link>
        <Link to={`${base}/subscriptions`} className="btn btn-secondary">Subscriptions</Link>
        <Link to={`${base}/clients`} className="btn btn-secondary">Clients</Link>
        <Link to={`${base}/reporting`} className="btn btn-secondary">View Reports</Link>
      </div>

      {/* Recent transactions */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18 }}>Recent Transactions</h3>
          <Link to={`${base}/transactions`} className="btn btn-secondary btn-sm">View All</Link>
        </div>
        {recentTransactions.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0', textAlign: 'center' }}>
            No transactions yet. Create a product and share the payment link to get started.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Description</th><th>Gross</th><th>GST</th><th>HMA Cut</th><th>You Keep</th><th>Date</th></tr>
            </thead>
            <tbody>
              {recentTransactions.map(t => (
                <tr key={t.id}>
                  <td>{t.description || 'Payment'}</td>
                  <td>{fmt(t.gross_amount)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{fmt(t.gst_amount)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{fmt(t.hma_amount)}</td>
                  <td style={{ color: 'var(--success)', fontWeight: 500 }}>{fmt(t.coach_amount)}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{new Date(t.created_at).toLocaleDateString('en-CA')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
